import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

export interface AiChatMessage {
  text: string;
}

@Component({
  selector: 'app-ai-chat',
  imports: [],
  template: `
    <div class="ai-chat-container" #chatContainer>
      @for (message of displayedMessages; track message) {
        <div class="ai-message">
          <div class="avatar-header">
            <div class="ai-avatar">
              <span class="ri-robot-2-line"></span>
            </div>
            <div class="ai-title" i18n="@@aiCoachTitle">AI Coach</div>
          </div>
          <div class="ai-content message bg-light-green-color border-radius">
            {{ message.text }}
          </div>
        </div>
      }
      @if (isTyping) {
        <div class="ai-message typing-message">
          <div class="avatar-header">
            <div class="ai-avatar">
              <span class="ri-robot-2-line"></span>
            </div>
            <div class="ai-title" i18n="@@aiCoachTitle">AI Coach</div>
          </div>
          <div
            class="typing-indicator message bg-light-green-color border-radius"
            i18n="@@aiCoachTyping"
          >
            Typing...
          </div>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .ai-chat-container {
        display: flex;
        flex-direction: column;
        gap: 15px;
        padding: 8px 0;
      }

      .ai-message {
        display: flex;
        flex-direction: column;
        gap: 5px;
      }

      .avatar-header {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .ai-avatar {
        font-size: 20px;
        color: var(--green100);
      }

      .ai-title {
        font-size: 14px;
        font-weight: 500;
        color: var(--black80Dynamic);
      }

      .message {
        padding: 10px 14px;
        text-align: left;
        border: 1px solid var(--green100);
        transition: 0.3s;
        font-size: 15px;
        font-weight: 300;
      }

      .ai-content {
        line-height: 1.5;
      }

      .typing-indicator {
        font-style: italic;
        color: var(--black55Dynamic);
      }
    `,
  ],
})
export class AiChatComponent implements OnInit, OnDestroy {
  @Input() messages: AiChatMessage[] = [];
  @Output() onComplete = new EventEmitter<void>();
  @Output() onNewMessage = new EventEmitter<AiChatMessage>();

  displayedMessages: AiChatMessage[] = [];
  isTyping = false;
  currentIndex = 0;

  // Store timeout references for cleanup
  private timeouts: number[] = [];

  @ViewChild('chatContainer') chatContainer!: ElementRef;

  ngOnInit() {
    if (this.messages && this.messages.length > 0) {
      // Show first message immediately
      this.displayedMessages.push(this.messages[0]);
      this.currentIndex = 1;

      // Emit the first message
      this.onNewMessage.emit(this.messages[0]);
      this.scrollToBottom();

      // If there are more messages, schedule them
      if (this.currentIndex < this.messages.length) {
        const timeoutId = setTimeout(() => {
          this.showNextMessage();
        }, 700);
        this.timeouts.push(timeoutId);
      } else {
        this.onComplete.emit();
      }
    }
  }

  ngOnDestroy() {
    // Clear all pending timeouts
    this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.timeouts = [];
  }

  private showNextMessage() {
    this.isTyping = true;
    this.scrollToBottom();

    const typingTimeoutId = setTimeout(() => {
      this.isTyping = false;
      this.displayedMessages.push(this.messages[this.currentIndex]);

      // Emit the new message event
      this.onNewMessage.emit(this.messages[this.currentIndex]);

      this.currentIndex++;
      this.scrollToBottom();

      if (this.currentIndex < this.messages.length) {
        const nextMessageTimeoutId = setTimeout(() => {
          this.showNextMessage();
        }, 700);
        this.timeouts.push(nextMessageTimeoutId);
      } else {
        this.onComplete.emit();
      }
    }, 1800);
    this.timeouts.push(typingTimeoutId);
  }

  private scrollToBottom() {
    const scrollTimeoutId = setTimeout(() => {
      // Scroll the page to the bottom of the viewport
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
    }, 100);
    this.timeouts.push(scrollTimeoutId);
  }
}
